import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();

  const sections = [
    { id: 'introduction', title: 'Introduction', icon: '🔒' },
    { id: 'information-collect', title: 'Information We Collect', icon: '📊' },
    { id: 'how-we-use', title: 'How We Use Your Information', icon: '🚀' },
    { id: 'contact', title: 'Contact Us', icon: '📮' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Back to Login</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <div className="flex items-center space-x-2">
              <Link 
                to="/terms-of-use" 
                className="px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
              >
                Terms of Use
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Table of Contents */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📋</span>
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 flex items-center ${
                        activeSection === section.id
                          ? 'bg-purple-100 text-purple-700 font-medium border-l-4 border-purple-500'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-8">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-20 translate-x-20"></div>
              <div className="relative">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white p-3 rounded-xl">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      Privacy Policy
                    </h1>
                    <p className="text-gray-600 mt-2">Your privacy and data protection rights</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 bg-purple-50 px-6 py-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0v12a2 2 0 002 2h4a2 2 0 002-2V7m-6 0V6" />
                    </svg>
                    <span className="text-purple-900 font-semibold">Effective Date:</span>
                  </div>
                  <span className="text-purple-700 font-medium">March 1, 2025</span>
                </div>
              </div>
            </div>

            {/* Introduction Section */}
            <section id="introduction" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-purple-100 text-purple-700 p-3 rounded-xl">
                  <span className="text-2xl">🔒</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
                  <p className="text-gray-600 mt-1">Our commitment to protecting your privacy</p>
                </div>
              </div>
              <div className="ml-16">
                <div className="bg-purple-50 p-6 rounded-xl border-l-4 border-purple-500">
                  <p className="text-gray-700 leading-relaxed">
                    Welcome to <span className="font-semibold text-purple-700">Star Solutions</span> ("Company", "we", "our", "us"). 
                    We are committed to protecting your privacy and ensuring that your personal information is handled securely. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application (the "Service").
                  </p>
                </div>
              </div>
            </section>

            {/* Information We Collect */}
            <section id="information-collect" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-blue-100 text-blue-700 p-3 rounded-xl">
                  <span className="text-2xl">📊</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
                  <p className="text-gray-600 mt-1">Types of data we gather to provide our service</p>
                </div>
              </div>
              <div className="ml-16 space-y-6">
                <div className="grid gap-6">
                  <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-200 p-3 rounded-lg">
                        <span className="text-2xl">📝</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-blue-900 mb-3">Personal Information</h3>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <span className="text-gray-700">Name and contact details</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <span className="text-gray-700">Email address and phone number</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <span className="text-gray-700">Business details and registration information</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                    <div className="flex items-start space-x-4">
                      <div className="bg-green-200 p-3 rounded-lg">
                        <span className="text-2xl">📈</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-green-900 mb-3">Usage Data</h3>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-gray-700">IP address and browser information</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-gray-700">Device information and operating system</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-gray-700">Application usage patterns and log data</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                    <div className="flex items-start space-x-4">
                      <div className="bg-orange-200 p-3 rounded-lg">
                        <span className="text-2xl">🍪</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-orange-900 mb-3">Cookies & Tracking</h3>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            <span className="text-gray-700">Session cookies for authentication</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            <span className="text-gray-700">Analytics cookies for user experience</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            <span className="text-gray-700">Preference cookies for customization</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section id="how-we-use" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-green-100 text-green-700 p-3 rounded-xl">
                  <span className="text-2xl">🚀</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
                  <p className="text-gray-600 mt-1">The purposes for which we process your data</p>
                </div>
              </div>
              <div className="ml-16">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="bg-blue-500 text-white p-2 rounded-lg">
                        <span className="text-lg">🚀</span>
                      </div>
                      <h3 className="text-lg font-semibold text-blue-900">Service Operations</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Provide, operate, and maintain our Service to deliver the best possible experience for our users.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="bg-purple-500 text-white p-2 rounded-lg">
                        <span className="text-lg">💡</span>
                      </div>
                      <h3 className="text-lg font-semibold text-purple-900">User Experience</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Improve and enhance our web application based on user feedback and usage patterns.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="bg-green-500 text-white p-2 rounded-lg">
                        <span className="text-lg">📨</span>
                      </div>
                      <h3 className="text-lg font-semibold text-green-900">Communications</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Send updates, support messages, and promotional materials relevant to our services.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border border-red-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="bg-red-500 text-white p-2 rounded-lg">
                        <span className="text-lg">🛡️</span>
                      </div>
                      <h3 className="text-lg font-semibold text-red-900">Security</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Ensure security, prevent fraud, and protect against unauthorized access to our platform.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-8 shadow-xl">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-white/20 p-3 rounded-xl">
                  <span className="text-2xl">📮</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Contact Us</h2>
                  <p className="text-indigo-100 mt-1">Questions about your privacy? We're here to help</p>
                </div>
              </div>
              <div className="ml-16">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                  <p className="text-indigo-100 mb-6 font-medium">
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 bg-white/10 p-4 rounded-lg">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">🏢</span>
                      </div>
                      <div className="text-white">
                        <p className="font-semibold mb-1">Star Solutions</p>
                        <p className="text-sm text-indigo-100">Niraj Maheswari </p>
                        <p className="text-sm text-indigo-100 mt-2">
                          84, Bylane No 6, Borthakur Mill Road,<br/>
                          Ulubari, Guwahati - 781007,<br/>
                          Assam, India
                        </p>
                      </div>
                    </div>
                    <a 
                      href="mailto:info@starsolutions.biz" 
                      className="flex items-center space-x-4 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">📧</span>
                      </div>
                      <div>
                        <span className="font-medium underline">info@starsolutions.biz</span>
                        <p className="text-sm text-indigo-100">Email us for privacy-related questions</p>
                      </div>
                    </a>
                    <a 
                      href="tel:+918133047257" 
                      className="flex items-center space-x-4 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">📱</span>
                      </div>
                      <div>
                        <span className="font-medium">+91 8133047257</span>
                        <p className="text-sm text-indigo-100">Call us for immediate assistance</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Final Note */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-dashed border-gray-300 text-center">
              <div className="max-w-2xl mx-auto">
                <div className="bg-amber-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🔐</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Policy Updates</h3>
                <p className="text-gray-700">
                  We reserve the right to update this privacy policy periodically to reflect changes in our practices or legal requirements. 
                  Please review this page regularly for the latest information about our privacy practices.
                </p>
                <div className="mt-4 text-sm text-gray-500">
                  Last updated: March 1, 2025
                </div>
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Login</span>
              </button>
              <Link
                to="/terms-of-use"
                className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <span>Terms of Use</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;